import { defineStore } from 'pinia'
import {
    SUBSTITUTE_PARTS,
    REQUEST_REMOVE_SUBSTITUTE,
    APPROVE_BAD_SUBSTITUTE,
    BAD_SUBSTITUTE_REQUESTS,
    MASTER_SUBSTITUTES
} from '@/data/url'
import QuoteService from '@/services/quotes.service.js'
import apiClient from '@/services/apiClient'
import { unwrapApiData } from '@/services/apiEnvelope'

const normalizeParts = (partsList = []) => {
    return (partsList || [])
        .filter((part) => part != null)
        .map((part) => part.toString().trim().toUpperCase())
        .filter((part) => part.length > 0)
}

const getCacheStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage
        }
    } catch (err) {
        console.warn('Substitutes cache storage unavailable', err)
    }
    return null
}

const getCacheKey = (part) => `substitutes_${encodeURIComponent(part)}`

const readCachedTree = (part) => {
    const storage = getCacheStorage()
    if (!storage) return null

    try {
        const raw = storage.getItem(getCacheKey(part))
        if (!raw) return null

        const parsed = JSON.parse(raw)
        const expiresAt = parsed?.expiresAt
        if (typeof expiresAt === 'number' && Date.now() > expiresAt) {
            storage.removeItem(getCacheKey(part))
            return null
        }

        const tree = parsed?.data
        if (tree && tree.root) return tree
    } catch (err) {
        console.warn('Failed to read substitutes cache', err)
    }

    return null
}

const writeCachedTree = (part, tree) => {
    const storage = getCacheStorage()
    if (!storage) return

    try {
        storage.setItem(
            getCacheKey(part),
            JSON.stringify({
                data: tree,
                expiresAt: Date.now() + 3 * 24 * 60 * 60 * 1000, // 3 days
            })
        )
    } catch (err) {
        console.warn('Failed to persist substitutes cache', err)
    }
}

const getBadSubstituteStatus = (verifiedByManager) => {
    if (verifiedByManager === null || verifiedByManager === undefined) {
        return 'pending'
    }

    return verifiedByManager ? 'approved' : 'denied'
}

const pendingStatsRequests = new Set()

export const useSubstitutesStore = defineStore('substitutes', {
    state: () => ({
        loading: false,
        error: null,
        treesByRoot: {},
        selectedSubstituteOnModal: '',
        statsByPart: {},
        treePageState: {
            partInput: '',
            activePart: '',
        },
        badSubstituteRequests: [],
        badSubstituteRequestsLoading: false,
        badSubstituteRequestsError: null,
        badSubstituteRequestStatus: {},
        master_substitutes: null,
    }),
    getters: {
        substituteTreeByPart: (state) => (part) => {
            if (!part) return null
            return state.treesByRoot?.[part] || null
        },
        substitutesLoading: (state) => state.loading,
        substitutesError: (state) => state.error,
        substituteStatsByPart: (state) => (part) => {
            if (!part) return null
            return state.statsByPart?.[part] || null
        },
        badSubstituteRequestStatusById: (state) => (id) => {
            if (!id) return { processing: false, error: null }
            const status = state.badSubstituteRequestStatus?.[id]
            return status || { processing: false, error: null }
        },
    },
    actions: {
        setMasterSubstitutes(master_subs) {
            this.master_substitutes = master_subs || null
        },
        resetHighlightedPart() {
            this.selectedSubstituteOnModal = ''
        },
        setSubstitutesLoading(value) {
            this.loading = Boolean(value)
        },
        setSubstitutesError(error) {
            this.error = error || null
        },
        setSubstituteTree(node) {
            if (!node || !node.root) return
            this.treesByRoot = {
                ...this.treesByRoot,
                [node.root]: node,
            }
        },
        setSelectedSubstituteOnModal(value) {
            this.selectedSubstituteOnModal = value || ''
        },
        setSubstituteStats({ part, stats }) {
            if (!part) return
            this.statsByPart = {
                ...this.statsByPart,
                [part]: stats || null,
            }
        },
        resetSubstituteStatsForRoot(parts = []) {
            if (!Array.isArray(parts) || !parts.length) return
            const partsSet = new Set(parts)
            this.statsByPart = Object.fromEntries(
                Object.entries(this.statsByPart || {}).filter(([key]) => !partsSet.has(key))
            )
        },
        setTreePageState(payload = {}) {
            const current = this.treePageState || {}
            const next = payload && typeof payload === 'object' ? payload : {}
            this.treePageState = {
                partInput:
                    typeof next.partInput === 'string' ? next.partInput : current.partInput || '',
                activePart:
                    typeof next.activePart === 'string' ? next.activePart : current.activePart || '',
            }
        },
        resetTreePageState() {
            this.treePageState = {
                partInput: '',
                activePart: '',
            }
        },
        setBadSubstituteRequestsLoading(value) {
            this.badSubstituteRequestsLoading = Boolean(value)
        },
        setBadSubstituteRequestsError(error) {
            this.badSubstituteRequestsError = error || null
        },
        setBadSubstituteRequests(payload = []) {
            this.badSubstituteRequests = Array.isArray(payload) ? payload : []
        },
        setBadSubstituteRequestStatus({ id, processing, error } = {}) {
            if (!id) return
            const currentStatus = this.badSubstituteRequestStatus || {}
            this.badSubstituteRequestStatus = {
                ...currentStatus,
                [id]: {
                    ...(currentStatus[id] || {}),
                    ...(typeof processing === 'boolean' ? { processing } : {}),
                    ...(error !== undefined ? { error: error || null } : {}),
                },
            }
        },
        applyBadSubstituteDecision({ id, verified_by_manager, updated_at } = {}) {
            if (!id) return
            const requests = this.badSubstituteRequests || []
            const index = requests.findIndex((entry) => entry?.id === id)
            if (index === -1) return

            const target = requests[index]
            const nextVerified =
                typeof verified_by_manager === 'boolean'
                    ? verified_by_manager
                    : target?.verified_by_manager
            const updatedEntry = {
                ...target,
                status: getBadSubstituteStatus(nextVerified),
                verified_by_manager: nextVerified,
                updated_at: updated_at || new Date().toISOString(),
            }

            this.badSubstituteRequests = [
                ...requests.slice(0, index),
                updatedEntry,
                ...requests.slice(index + 1),
            ]
        },
        resetBadSubstituteRequestStatus() {
            this.badSubstituteRequestStatus = {}
        },
        async clearMasterSubstitutes() {
            this.master_substitutes = null
        },
        async fetchMasterSubstitutes(part_numbers = []) {
            const URL = `${MASTER_SUBSTITUTES}?part_numbers=${encodeURIComponent(
                Array.isArray(part_numbers) ? part_numbers.join(',') : ''
            )}`
            try {
                const response = await apiClient.get(URL)
                const data = unwrapApiData(response, {})
                this.setMasterSubstitutes(data)
            } catch (error) {
                console.error('Failed loading substitutes tree', error)
                return {}
            }
        },
        async fetchSubstitutes(partsList = []) {
            const normalizedParts = normalizeParts(
                Array.isArray(partsList) ? partsList : [partsList]
            )
            if (!normalizedParts.length) return

            const uniqueParts = [...new Set(normalizedParts)]
            const partsToFetch = []

            uniqueParts.forEach((part) => {
                if (this.treesByRoot[part]) return
                const cached = readCachedTree(part)
                if (cached) {
                    this.setSubstituteTree(cached)
                } else {
                    partsToFetch.push(part)
                }
            })

            if (!partsToFetch.length) {
                this.setSubstitutesError(null)
                return
            }

            this.setSubstitutesLoading(true)
            this.setSubstitutesError(null)

            try {
                const response = await apiClient.post(SUBSTITUTE_PARTS, {
                    part_numbers: partsToFetch,
                })

                const data = unwrapApiData(response, [])
                const nodes = Array.isArray(data) ? data : []

                nodes.forEach((node) => {
                    if (node?.root) {
                        this.setSubstituteTree(node)
                        writeCachedTree(node.root, node)
                    }
                })
            } catch (error) {
                console.error('Failed loading substitutes tree', error)
                this.setSubstitutesError(error instanceof Error ? error.message : 'Unknown error')
            } finally {
                this.setSubstitutesLoading(false)
            }
        },
        async requestSubstituteRemoval(payload = {}) {
            const requestBody =
                payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}

            const response = await apiClient.post(REQUEST_REMOVE_SUBSTITUTE, requestBody)

            try {
                return unwrapApiData(response, null)
            } catch (err) {
                return null
            }
        },
        async fetchSubstituteStats(payload = []) {
            const partsInput = Array.isArray(payload?.parts) ? payload.parts : payload
            const partsArray = Array.isArray(partsInput) ? partsInput : [partsInput]
            const normalizedParts = normalizeParts(partsArray)
            if (!normalizedParts.length) return

            if (payload?.reset === true) {
                const cachedParts = Object.keys(this.statsByPart || {})
                const partsToRemove = cachedParts.filter((part) => !normalizedParts.includes(part))
                if (partsToRemove.length) {
                    this.resetSubstituteStatsForRoot(partsToRemove)
                }
            }

            const statsFromSearchStore =
                payload && typeof payload === 'object' && payload.statsData ? payload.statsData : {}
            const cachedStats = this.statsByPart || {}
            const partsMissingStats = []

            normalizedParts.forEach((partNumber) => {
                if (!partNumber) return
                const alreadyCached = Boolean(cachedStats[partNumber])
                if (alreadyCached) return

                const statsInSearchStore = statsFromSearchStore[partNumber]
                if (statsInSearchStore) {
                    this.setSubstituteStats({ part: partNumber, stats: statsInSearchStore })
                } else {
                    partsMissingStats.push(partNumber)
                }
            })

            if (partsMissingStats.length) {
                await this.fetchStatsFromApi(partsMissingStats, { vuexStore: payload?.vuexStore })
            }
        },
        async fetchStatsFromApi(parts = [], options = {}) {
            const uniqueParts = parts
                .map((part) => part?.toString().trim())
                .filter((part) => part && !pendingStatsRequests.has(part))

            if (!uniqueParts.length) return

            uniqueParts.forEach((part) => pendingStatsRequests.add(part))

            try {
                const response = await QuoteService.getQuoteAggregatedData(0,uniqueParts, options?.substitutes || null, options?.customer || "")

                const statsResult = response?.stats_result
                if (Array.isArray(statsResult)) {
                    statsResult.forEach((entry) => {
                        if (!entry?.part) return
                        if (options?.vuexStore && typeof options.vuexStore.commit === 'function') {
                            options.vuexStore.commit('setPartStatsData', entry)
                        }
                        this.setSubstituteStats({ part: entry.part, stats: entry })
                    })
                }
            } catch (error) {
                console.error('Failed fetching substitute stats', error)
            } finally {
                uniqueParts.forEach((part) => pendingStatsRequests.delete(part))
            }
        },
        async fetchBadSubstituteRequests() {
            this.setBadSubstituteRequestsLoading(true)
            this.setBadSubstituteRequestsError(null)

            try {
                const response = await apiClient.get(BAD_SUBSTITUTE_REQUESTS)

                const payload = unwrapApiData(response, [])
                const requests = Array.isArray(payload) ? payload : []
                const normalized = requests.map((entry) => {
                    const firstName = entry?.user_first_name || ''
                    const lastName = entry?.user_last_name || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    const status = getBadSubstituteStatus(entry?.verified_by_manager)
                    return {
                        ...entry,
                        user: {
                            first_name: firstName,
                            last_name: lastName,
                            full_name: fullName,
                        },
                        status,
                    }
                })
                this.setBadSubstituteRequests(normalized)
                this.resetBadSubstituteRequestStatus()
            } catch (error) {
                console.error('Failed to load bad substitute requests', error)
                this.setBadSubstituteRequestsError(
                    error instanceof Error ? error.message : 'Unknown error'
                )
            } finally {
                this.setBadSubstituteRequestsLoading(false)
            }
        },
        async reviewBadSubstituteRequest(payload = {}) {
            const id = payload?.id
            const approve = payload?.approve

            if (!id || typeof approve !== 'boolean') {
                throw new Error('The request id and approve flag are required')
            }

            this.setBadSubstituteRequestStatus({ id, processing: true, error: null })

            try {
                await apiClient.put(APPROVE_BAD_SUBSTITUTE, {
                    substitute_bad_id: id,
                    approve,
                    note: payload?.note || '',
                })

                this.applyBadSubstituteDecision({
                    id,
                    verified_by_manager: approve,
                    updated_at: new Date().toISOString(),
                })

                this.setBadSubstituteRequestStatus({ id, processing: false, error: null })
            } catch (error) {
                console.error('Failed to review bad substitute', error)
                this.setBadSubstituteRequestStatus({
                    id,
                    processing: false,
                    error: error instanceof Error ? error.message : 'Unknown error',
                })
                throw error
            }
        },
    },
})
